'use client';

import { useLayoutEffect, useRef } from 'react';
import gsap from 'gsap';
import cn from './style.module.scss';

type AnimationType =
  | 'zoomIn'
  | 'zoomOut'
  | 'slideInLeft'
  | 'slideInRight'
  | 'slideInTop'
  | 'slideInBottom';

interface ParallaxBlockProps {
  height?: string;
  className?: string;
  animationType?: AnimationType;
  animationDuration?: number;
  children?: React.ReactNode;
}

const getFromVars = (animationType: AnimationType): gsap.TweenVars => {
  switch (animationType) {
    case 'zoomOut':
      return { autoAlpha: 0, scale: 1.2 };
    case 'slideInLeft':
      return { autoAlpha: 0, x: -80 };
    case 'slideInRight':
      return { autoAlpha: 0, x: 80 };
    case 'slideInTop':
      return { autoAlpha: 0, y: -80 };
    case 'slideInBottom':
      return { autoAlpha: 0, y: 80 };
    case 'zoomIn':
    default:
      return { autoAlpha: 0, scale: 0.8 };
  }
};

const revealCallbacks = new WeakMap<Element, () => void>();
let sharedObserver: IntersectionObserver | null = null;

const getObserver = (): IntersectionObserver | null => {
  if (typeof window === 'undefined' || !('IntersectionObserver' in window)) {
    return null;
  }
  if (!sharedObserver) {
    sharedObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          const cb = revealCallbacks.get(entry.target);
          if (cb) {
            cb();
            revealCallbacks.delete(entry.target);
            sharedObserver?.unobserve(entry.target);
          }
        });
      },
      { rootMargin: '0px 0px 0px 0px', threshold: 0 }
    );
  }
  return sharedObserver;
};

const ParallaxBlock: React.FC<ParallaxBlockProps> = ({
  animationType = 'zoomIn',
  animationDuration = 1000,
  className = '',
  children,
}) => {
  const sectionRef = useRef<HTMLDivElement>(null);

  useLayoutEffect(() => {
    const el = sectionRef.current;
    if (!el) return;

    const fromVars = getFromVars(animationType);
    gsap.set(el, {
      ...fromVars,
      force3D: true,
      willChange: 'transform, opacity',
    });

    const reveal = () => {
      gsap.to(el, {
        autoAlpha: 1,
        x: 0,
        y: 0,
        scale: 1,
        duration: (animationDuration || 1000) / 1000,
        ease: 'power2.out',
        force3D: true,
        onComplete: () => gsap.set(el, { clearProps: 'willChange' }),
      });
    };

    const observer = getObserver();
    if (!observer) {
      reveal();
      return;
    }
    const rect = el.getBoundingClientRect();
    if (rect.top <= window.innerHeight * 0.9 && rect.bottom >= 0) {
      reveal();
      return;
    }

    revealCallbacks.set(el, reveal);
    observer.observe(el);

    return () => {
      revealCallbacks.delete(el);
      observer.unobserve(el);
      gsap.killTweensOf(el);
    };
  }, [animationType, animationDuration]);

  return (
    <div ref={sectionRef} className={`${cn.animatedBlock} ${className || ''}`}>
      {children}
    </div>
  );
};

export default ParallaxBlock;
